import React, { useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import VerifyEmail from './components/Onboarding/VerifyEmail';
import { AppRouter } from './pages';
import PanelLayout from './layout';
import { GraphQLProvider } from './providers/GraphQLProvider';
import './styles/index.css';
import { SettingsProvider, useCustomerSettings } from './contexts/CustomerSettingsContext';
import { LoadingSpinner } from './components/Utils';
import SignupForm from './components/Onboarding/Signup';
import Login from './components/Onboarding/Login';
import PaymentSuccess from './components/Onboarding/PaymentSuccess';
import { SubscriptionPlansList } from './components/Onboarding/SubscriptionPlan';
import PaymentCancelled from './components/Onboarding/PaymentCancelled';
import { appConfig } from './app-config';
import { useAuth } from './hooks/useAuth';
import UnverifiedEmailError from './components/Onboarding/UnverifiedEmailError';

const stripePromise = loadStripe(appConfig.stripe.publishableKey ?? '');

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, loginAndRedirectBack, user } = useAuth(); // Use your custom hook
  const navigate = useNavigate();
  const [hasRedirected, setHasRedirected] = useState(false);

  useEffect(() => {
    const handleAuth = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const authError = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');

      if (authError === 'access_denied' && errorDescription?.includes('Please verify your email')) {
        navigate('/unverified-email', {
          // Updated path
          state: {
            error: errorDescription,
            isVerificationError: true,
          },
        });
        return;
      }

      if (!isLoading && !isAuthenticated) {
        try {
          await loginAndRedirectBack(window.location.pathname);
        } catch (error) {
          console.error('Login error:', error);
        }
      }
    };

    handleAuth();
  }, [isLoading, isAuthenticated, navigate, loginAndRedirectBack]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated && user?.email_verified) {
    const customerSettingsMethods = useCustomerSettings();
    if (
      customerSettingsMethods?.customer?.status === 'pending' &&
      customerSettingsMethods?.customer?.currentSubscription === null
    ) {
      return <SubscriptionPlansList />;
    }

    if (customerSettingsMethods?.customer?.currentSubscription !== null && !hasRedirected) {
      setHasRedirected(true);
      navigate('/new-controller-wizard');
    }
  }

  return isAuthenticated && user?.email_verified ? children : null;
};

function AppContent() {
  const { isLoading } = useAuth0();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Routes>
      {/* Public route for signup */}
      <Route path="/signup" element={<SignupForm />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/unverified-email" element={<UnverifiedEmailError />} />
      <Route path="/login" element={<Login />} />
      <Route path="/payment-cancelled" element={<PaymentCancelled />} />
      <Route path="/success" element={<PaymentSuccess />} />
      {/* Protected routes for authenticated users */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <PanelLayout>
              <AppRouter />
            </PanelLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <Elements stripe={stripePromise}>
      <GraphQLProvider>
        <SettingsProvider>
          <AppContent />
        </SettingsProvider>
      </GraphQLProvider>
    </Elements>
  );
}

export default App;
